<template>
  <!-- eslint-disable vue/attribute-hyphenation -->
  <div
    id="recommendedPoliciesCarousel"
    class="recommended-items-carousel"
  >
    <vueper-slides
      v-if="renderCards"
      :arrowsOutside="true"
      :bullets="false"
      :bulletsOutside="false"
      :class="['no-shadow', $style.slideContainer]"
      :disable-arrows-on-edges="false"
      :fixed-height="carouselHeight"
      :gap="1.9"
      :slideMultiple="numberOfVisibleSlides > 1"
      :touchable="false"
      :visible-slides="numberOfVisibleSlides"
      @ready="setNumberOfVisibileSlides"
    >
      <template #arrow-left>
        <div
          :class="[$style.leftArrow, {[$style.disableArrow]: hoveringCard}]"
        >
          <ArrowLeft
            :class="$style.arrow"
          />
        </div>
      </template>
      <template #arrow-right>
        <div
          :class="[$style.rightArrow, {[$style.disableArrow]: hoveringCard}]"
        >
          <ArrowRight
            :class="$style.arrow"
          />
        </div>
      </template>
      <vueper-slide
        v-for="policy in policyDataFiltered"
        :key="policy.title"
        :class="$style.slide"
        @mouseleave.native="hoveringCard = false"
        @mouseover.native="hoveringCard = true"
      >
        <template v-slot:content>
          <SeeAllFeaturesCard
            v-if="policy.seeAllCard"
            :card="{
              description: policy.description,
              handleSeeAllButtonClick
            }"
          />
          <RecommendedPolicyCard
            v-else
            v-show="policy.showItem"
            :availability="policy.availability"
            :description="policy.description"
            :title="policy.title"
          />
        </template>
      </vueper-slide>
    </vueper-slides>
  </div>
</template>

<script>
import 'vueperslides/dist/vueperslides.css';
import * as Constants from 'client/util/Constants';
import { VueperSlide, VueperSlides } from 'vueperslides';
import { get } from 'lodash';
import { mapGetters } from 'vuex';
import ArrowLeft from 'public/img/growth/arrow-left.svg?inline';
import ArrowRight from 'public/img/growth/arrow-right.svg?inline';
import ItemType from 'client/util/Constants/ItemType';
import RecommendedPoliciesMixin from 'client/policies/RecommendedPoliciesMixin';
import RecommendedPolicyCard from 'client/policies/RecommendedPolicyCard.vue';
import SeeAllFeaturesCard from 'client/components/FeaturedItemsCarousel/cards/SeeAllFeaturesCard.vue';
import Util from 'client/util/Util';

const { ConfigurationItemType } = ItemType;
const configurationItem = new ConfigurationItemType();

const {
  ItemTypeConsts,
  StoreModuleMap,
} = Constants;

const onboardingModule = 'OnboardingModule';
const policyCollection = StoreModuleMap[ItemTypeConsts.configuration];

export default {
  name: 'RecommendedPoliciesCarousel',

  components: {
    ArrowLeft,
    ArrowRight,
    RecommendedPolicyCard,
    SeeAllFeaturesCard,
    VueperSlides,
    VueperSlide,
  },

  mixins: [
    RecommendedPoliciesMixin,
  ],

  props: {
    carouselHeight: {
      type: String,
      default: '180px',
    },

    itemType: {
      type: String,
      default: ItemTypeConsts.configuration,
    },
  },

  data() {
    return {
      hoveringCard: false,
      numberOfVisibleSlides: 5,
      renderCards: false,
      resizeHandler: () => {},
    };
  },

  computed: {
    ...mapGetters(onboardingModule, {
      implementationChecklistDismissed: 'getImplementationChecklistDismissed',
    }),

    ...mapGetters(policyCollection, {
      items: 'getCollection',
    }),
  },

  watch: {
    implementationChecklistDismissed() {
      this.setNumberOfVisibileSlides();
    },

    items(newVal, oldVal) {
      // when items (policies in this case) refreshes, we need to refresh our remaining
      // policies data. We only fire refreshRemainingPolicies if either the old value or new value
      // has length to avoid multiple unnecessary calls.
      if (newVal.length || oldVal.length) {
        this.refreshRemainingPolicies();
      }
    },
  },

  async created() {
    await this.initRemainingPolicies();
    // template will try to render the cards and the policies will still not be loaded
    // so we add this to ensure that initRemainingPolicies is done and the the cards are ready
    this.renderCards = true;
    this.$emit('remainingPoliciesLengthCheck', this.policyDataFiltered.length);
  },

  mounted() {
    this.resizeHandler = () => {
      this.setNumberOfVisibileSlides();
    };
    window.addEventListener('resize', this.resizeHandler);
  },

  updated() {
    this.$emit('remainingPoliciesLengthCheck', this.policyDataFiltered.length);
  },

  destroyed() {
    window.removeEventListener('resize', this.resizeHandler);
  },

  methods: {
    calculateNumberOfVisibleSlides() {
      const parentWidth = this.getParentWidth();

      if (parentWidth > 1050) return 5;
      if (parentWidth > 950) return 4;
      if (parentWidth > 625) return 3;
      if (parentWidth > 500) return 2;
      return 1;
    },

    getParentWidth() {
      // The parent div isn't defined if setNumberOfVisibileSlides is called too early
      // Calling setNumberOfVisibileSlides on @ready solves this but we still want a default
      // if the parent div isn't defined we want to default based on window size
      // this adjustment takes into account the implementation checklist and left nav
      const widthAdjustment = this.implementationChecklistDismissed ? 350 : 650;
      const windowSize = window.innerWidth - widthAdjustment;
      const carouselDiv = document.getElementById('recommendedPoliciesCarousel');
      // if parentElement.offsetWidth is undefined use a rough estimate based on window size

      // use || instead of get default to avoid returning 0
      return get(carouselDiv, 'parentElement.offsetWidth') || windowSize;
    },

    handleSeeAllButtonClick() {
      Util.redirect(`${configurationItem.route}/choose/recommended`);
    },

    setNumberOfVisibileSlides() {
      this.numberOfVisibleSlides = this.calculateNumberOfVisibleSlides();
    },
  },
};
</script>

<style module>

@value (jcGrey200) from '~css/brand-colors.css';

.slide,
.slideContainer,
.slideContainer div:first-of-type {
  max-height: 180px;
}

.leftArrow,
.rightArrow {
  align-content: center;
  align-items: center;
  background-color: white;
  display: flex;
  height: 180px;
  width: 92px;
}

.leftArrow {
  left: 0;
}

.rightArrow {
  right: 0;
}

.slideContainer .arrow {
  margin: 0 auto;
  overflow: unset;
  padding: 0;
  stroke: none;
}

:global(.vueperslides__arrow) .disableArrow {
  display: none;
}
</style>
